import type { ApiErrorBody } from '@bruhat/shared';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  readonly statusCode: number;
  readonly code?: string;
  readonly details?: unknown;
  /** Field-level messages from class-validator, when present. */
  readonly fieldErrors: string[];

  constructor(body: ApiErrorBody) {
    const message = Array.isArray(body.message) ? body.message[0] : body.message;
    super(message ?? 'Request failed');
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.code = body.code;
    this.details = body.details;
    this.fieldErrors = Array.isArray(body.message) ? body.message : [];
  }
}

interface Session {
  accessToken: string | null;
  refreshToken: string | null;
  companyId: string | null;
}

/**
 * Session lives here rather than in React state so that a 401 handler deep in
 * a fetch can read and rotate tokens without a hook.
 */
const session: Session = {
  accessToken: localStorage.getItem('bruhat.accessToken'),
  refreshToken: localStorage.getItem('bruhat.refreshToken'),
  companyId: localStorage.getItem('bruhat.companyId'),
};

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((l) => l());
}

export function getSession(): Readonly<Session> {
  return session;
}

export function setTokens(accessToken: string | null, refreshToken: string | null) {
  session.accessToken = accessToken;
  session.refreshToken = refreshToken;
  if (accessToken) localStorage.setItem('bruhat.accessToken', accessToken);
  else localStorage.removeItem('bruhat.accessToken');
  if (refreshToken) localStorage.setItem('bruhat.refreshToken', refreshToken);
  else localStorage.removeItem('bruhat.refreshToken');
  notify();
}

export function setActiveCompany(companyId: string | null) {
  session.companyId = companyId;
  if (companyId) localStorage.setItem('bruhat.companyId', companyId);
  else localStorage.removeItem('bruhat.companyId');
  notify();
}

export function clearSession() {
  setTokens(null, null);
  setActiveCompany(null);
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the company header for endpoints that are not tenant-scoped. */
  withoutCompany?: boolean;
  idempotencyKey?: string;
  /** Return the raw Response — used for file downloads. */
  raw?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/** A single in-flight refresh, so a burst of 401s does not stampede. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (!session.refreshToken) return false;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        });
        if (!response.ok) {
          clearSession();
          return false;
        }
        const data = (await response.json()) as { accessToken: string; refreshToken: string };
        setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        // Release the lock on the next tick so concurrent callers all see the result.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }

  return refreshInFlight;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, withoutCompany, idempotencyKey, raw, query, ...init } = options;

  const send = async (): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (session.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`);
    if (!withoutCompany && session.companyId) headers.set('X-Company-Id', session.companyId);
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);

    let payload: BodyInit | undefined;
    if (body instanceof FormData) {
      payload = body; // let the browser set the multipart boundary
    } else if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
      payload = JSON.stringify(body);
    }

    return fetch(buildUrl(path, query), { ...init, headers, body: payload });
  };

  let response = await send();

  // One transparent retry after a token refresh.
  if (response.status === 401 && session.refreshToken) {
    const refreshed = await refreshTokens();
    if (refreshed) response = await send();
  }

  if (!response.ok) {
    let errorBody: ApiErrorBody;
    try {
      errorBody = (await response.json()) as ApiErrorBody;
    } catch {
      errorBody = {
        statusCode: response.status,
        error: response.statusText,
        message: `Request failed with status ${response.status}`,
        timestamp: new Date().toISOString(),
        path,
      };
    }
    if (response.status === 401) clearSession();
    throw new ApiError(errorBody);
  }

  if (raw) return response as unknown as T;
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // An endpoint that returns null (e.g. "no AI origin for this voucher")
    // arrives as an empty, untyped body. That is "nothing", not a download —
    // handing back an empty Blob would make callers think data came back.
    const blob = await response.blob();
    return (blob.size === 0 ? null : blob) as unknown as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

/** Fetch a report as a file and hand it to the browser. */
export async function downloadFile(
  path: string,
  query: RequestOptions['query'],
  fileName: string,
): Promise<void> {
  const response = await apiRequest<Response>(path, { method: 'GET', query, raw: true });
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
